import { array, hash } from '@ember/helper';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import BvInlineEdit from 'volta/components/bv-inline-edit';
import BvMenu from 'volta/components/bv-menu';
import BvSelect from 'volta/components/bv-select';
import BvSelectMulti from 'volta/components/bv-select-multi';
import BvSpinner from 'volta/components/bv-spinner';
import PoWorkbenchPrioritySelectItem from 'volta/components/po-workbench/priority-select/item';
import { POPriorityOptions, POPriorityOptionsDict } from 'volta/models/planning-order';
import { translateFilterOptions } from 'volta/utils/filters-utils';

import type { ContentValue } from '@glint/template';
import type { IntlService } from 'ember-intl';
import type { BvFocusUnitModifier } from 'volta/components/bv-focus-zone';
import type { ILabelAction } from 'volta/components/bv-labelled';
import type { IBvMenuItem } from 'volta/components/bv-menu';
import type { BvSelectArgs } from 'volta/components/bv-select';
import type { TPOPriorityOption, TPOPriorityOptionValue } from 'volta/models/types/planning-order';
import type LabelService from 'volta/services/label-service';

interface PoWorkbenchPrioritySelectSignature {
  Args: {
    autoComponent?: ContentValue;
    stopPropagation?: boolean;
    focusZoneUnit?: BvFocusUnitModifier;
    onSave?: (e: Event) => void;
    inline?: boolean;
    minimized?: boolean;
    disabled?: boolean;
    renderInPlace?: boolean;
    matchTriggerWidth?: boolean;
    horizontalPosition?: BvSelectArgs['horizontalPosition'];
    allowClear?: boolean;
    labelAction?: ILabelAction;
    label?: string;
    fill?: boolean;
    isLoading?: boolean;
    multiple?: boolean;
    value?: TPOPriorityOptionValue | null;
    values?: TPOPriorityOptionValue[];
    displayClassNames?: string;
    onChange?: (value: TPOPriorityOptionValue) => void;
    onChangeMany?: (values: TPOPriorityOptionValue[]) => void;
    enableFocusTrap?: boolean;
  };
  Blocks: {
    trigger: [unknown];
  };
  Element: HTMLElement;
}

export default class PoWorkbenchPrioritySelectComponent extends Component<PoWorkbenchPrioritySelectSignature> {
  @service intl!: IntlService;
  @service labelService!: LabelService;

  options = translateFilterOptions(POPriorityOptions, this.labelService);
  optionsByValue = POPriorityOptionsDict;

  get selectedOption() {
    const { value } = this.args;
    return isPresent(value) ? POPriorityOptionsDict[`${value}`] : undefined;
  }

  get selectedOptions() {
    const { values = [] } = this.args;
    const valuesAsStr = new Set(values.map((v) => v.toString()));
    return values.length > 0 ? this.options.filter((o) => valuesAsStr.has(o.value.toString())) : [];
  }

  getOptions = (keys: string[], afterAction?: () => void) =>
    POPriorityOptions.reduce<IBvMenuItem[]>((acc, { key, icon, iconColor, value }) => {
      if (keys.length > 0 && !keys.includes(key)) {
        return acc;
      }
      acc.push({
        icon,
        iconColor,
        content: this.intl.t(key),
        selected: this.selectedOption?.value === value,
        onClick: async (_event: Event) => {
          await this.args.onChange?.(value);
          afterAction?.();
        }
      });
      return acc;
    }, []);

  handleChange = (option: TPOPriorityOption) => {
    return this.args.onChange?.(option.value);
  };

  handleChangeMany = (values: TPOPriorityOption[]) => {
    return this.args.onChangeMany?.(values.map((v) => v.value));
  };

  <template>
    {{#if @multiple}}
      <BvSelectMulti
        @options={{this.options}}
        @selected={{this.selectedOptions}}
        @onChange={{this.handleChangeMany}}
        @isLoading={{@isLoading}}
        @fill={{@fill}}
        @label={{@label}}
        @labelAction={{@labelAction}}
        @allowClear={{@allowClear}}
        @searchEnabled={{true}}
        @searchField='label'
        @matchTriggerWidth={{@matchTriggerWidth}}
        @horizontalPosition={{@horizontalPosition}}
        @renderInPlace={{@renderInPlace}}
        @disabled={{@disabled}}
        @placeholder={{t 'priority'}}
        ...attributes
        as |opt|
      >
        <PoWorkbenchPrioritySelectItem
          @minimized={{@minimized}}
          @text={{opt.label}}
          @icon={{opt.icon}}
          @color={{opt.iconColor}}
        />
      </BvSelectMulti>
    {{else if @inline}}
      <BvInlineEdit
        @isMenu={{true}}
        @preventScroll={{true}}
        @hideDefaultActions={{true}}
        @horizontalPosition={{@horizontalPosition}}
        @disabled={{@disabled}}
        @onSave={{@onSave}}
        @focusZoneUnit={{@focusZoneUnit}}
        @stopPropagation={{@stopPropagation}}
        @matchTriggerWidth={{@matchTriggerWidth}}
        @renderInPlace={{@renderInPlace}}
        @displayClassNames={{@displayClassNames}}
        @enableFocusTrap={{@enableFocusTrap}}
        ...attributes
      >
        <:default as |d|>
          {{#if (has-block 'trigger')}}
            {{yield (hash popover=d.popover selected=this.selectedOption) to='trigger'}}
          {{else}}
            <PoWorkbenchPrioritySelectItem
              @minimized={{@minimized}}
              @text={{this.selectedOption.key}}
              @icon={{this.selectedOption.icon}}
              @color={{this.selectedOption.iconColor}}
            />
          {{/if}}
          {{#if @isLoading}}
            <div class='bv-spinner-container u-bg-surface u-border-radius'>
              <BvSpinner @size='xs' />
            </div>
          {{/if}}
        </:default>
        <:editing as |e|>
          <BvMenu
            @sections={{array
              (hash items=(this.getOptions (array 'none') e.close))
              (hash
                items=(this.getOptions
                  (array 'blocker' 'critical' 'high' 'medium' 'low' 'trivial') e.close
                )
              )
            }}
          />
        </:editing>
      </BvInlineEdit>
    {{else}}
      <BvSelect
        @options={{this.options}}
        @selected={{this.selectedOption}}
        @onChange={{this.handleChange}}
        @isLoading={{@isLoading}}
        @fill={{@fill}}
        @label={{@label}}
        @labelAction={{@labelAction}}
        @allowClear={{@allowClear}}
        @matchTriggerWidth={{@matchTriggerWidth}}
        @renderInPlace={{@renderInPlace}}
        @disabled={{@disabled}}
        @placeholder={{t 'priority'}}
        ...attributes
        as |opt|
      >
        <PoWorkbenchPrioritySelectItem
          @minimized={{@minimized}}
          @text={{opt.key}}
          @icon={{opt.icon}}
          @color={{opt.iconColor}}
        />
      </BvSelect>
    {{/if}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'PoWorkbench::PrioritySelect': typeof PoWorkbenchPrioritySelectComponent;
    'po-workbench/priority-select': typeof PoWorkbenchPrioritySelectComponent;
  }
}
